// src/components/CustomerFlow/Steps/IdentityValidationStep.js

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIdCard, faExternalLinkAlt, faExclamationTriangle, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import api from '../../../services/api';
import { trackMobileIdentityValidationCompleted } from '../../../analytics/events';

const IdentityValidationStep = ({ 
  flowData, 
  stepStatuses,
  onNext,
  searchParams
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [validationData, setValidationData] = useState({
    verification_url: null,
    testing_url: null
  });
  const [validationError, setValidationError] = useState(null);
  const [isReturningFromExternal, setIsReturningFromExternal] = useState(false);
  const [hasProcessedExternalReturn, setHasProcessedExternalReturn] = useState(false);
  const [hasAutoActivated, setHasAutoActivated] = useState(false);

  // Función para limpiar el parámetro from_step de la URL
  const cleanUrlParameter = () => {
    if (searchParams?.get('from_step') === 'identity_validation') {
      const url = new URL(window.location.href);
      url.searchParams.delete('from_step');
      
      // Actualizar la URL sin el parámetro, sin recargar la página
      window.history.replaceState({}, document.title, url.toString());
    }
  };

  // Auto-activar el paso si está en pending (solo una vez)
  useEffect(() => {
    const currentStepStatus = stepStatuses['ValidaStep'];
    
    // Si el estado de ValidaStep es pending:
    if (currentStepStatus === 'pending' && !hasAutoActivated) {
      // Marcar como auto-activado para evitar re-ejecución
      setHasAutoActivated(true);
      
      // Llamar a la API para marcar el step como in_progress
      onNext({
        step_name: 'ValidaStep',
        step_status: 'in_progress',
        auto_activated: true,
        activation_timestamp: new Date().toISOString()
      }, 'in_progress');
    }
  }, [stepStatuses, onNext, hasAutoActivated]);

  // Verificar si venimos del servicio externo de validación
  useEffect(() => {
    const fromStep = searchParams?.get('from_step');
    const currentStepStatus = stepStatuses['ValidaStep'];
    
    // Si venimos de identity_validation y el paso está in_progress, 
    // significa que ya completamos la validación externa
    if (fromStep === 'identity_validation' && currentStepStatus === 'in_progress' && !hasProcessedExternalReturn) {
      setIsReturningFromExternal(true);
      setHasProcessedExternalReturn(true); // Marcar como procesado para evitar re-ejecución
      
      // Limpiar el parámetro de la URL inmediatamente
      cleanUrlParameter();
      
      handleExternalValidationComplete();
    } else if (!hasProcessedExternalReturn && !isReturningFromExternal) {
      // Solo obtener el enlace si no estamos procesando un retorno externo
      // y no hemos procesado ya un retorno externo
      fetchValidationLink();
    }
  }, [searchParams, stepStatuses, hasProcessedExternalReturn, isReturningFromExternal]);

  // Obtener el enlace de validación
  const fetchValidationLink = async () => {
    try {
      setIsLoading(true);
      setValidationError(null);

      const response = await api.get(`/customers/${flowData.tokenCustomerId}/purchase-requests/${flowData.tokenPurchaseRequestId}/self-service/identity-validation`);
      
      if (response.data.success) {
        // La API puede devolver verification_url como null pero tener testing_url
        const { verification_url, testing_url } = response.data.data;
        
        setValidationData({
          verification_url: verification_url || null,
          testing_url: testing_url || null
        });
        
        // Si no hay ninguna URL disponible, mostrar error
        if (!verification_url && !testing_url) {
          throw new Error('No se pudo obtener ningún enlace de validación');
        }
      } else {
        throw new Error('No se pudo obtener el enlace de validación');
      }
    } catch (error) {
      console.error('Error fetching validation link:', error);
      setValidationError('No pudimos obtener el enlace. Intentá de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  // Manejar la completación de la validación externa
  const handleExternalValidationComplete = async () => {
    try {
      // Mostrar el mensaje de éxito por 3 segundos
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Después de esperar, marcar como loading y pasar al siguiente paso
      setIsLoading(true);

      // Track identity validation completed
      const branchId = localStorage.getItem('temp_tokenBranchId');
      trackMobileIdentityValidationCompleted({
        commerceId: branchId || 'mobile_flow', // Fallback
        branchId: branchId || undefined,
        flowId: flowData?.flow_id
      });
      
      // Marcar el paso como completado
      onNext({ 
        step_name: 'ValidaStep',
        step_status: 'processed',
        external_validation_completed: true,
        completion_timestamp: new Date().toISOString(),
        completed_from_external: true
      }, 'processed');
      
    } catch (error) {
      console.error('Error procesando la validación externa:', error);
      setValidationError('No pudimos terminar este paso. Recargá la página e intentá de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExternalValidation = () => {
    if (validationData.verification_url) {
      
      // Cambiar el estado del paso a in_progress antes de redirigir
      onNext({ 
        step_name: 'ValidaStep',
        step_status: 'in_progress',
        external_validation_started: true,
        validation_timestamp: new Date().toISOString(),
        validation_link: validationData.verification_url
      }, 'in_progress');
      
      // Redirigir al usuario
      window.location.href = validationData.verification_url;
    }
  };

  // Si estamos retornando del servicio externo, mostrar mensaje de completado
  if (isReturningFromExternal) {
    return (
      <div className="identity-validation-step bg-white p-4 rounded-3">
        <div className="text-center">
          <FontAwesomeIcon icon={faCheckCircle} size="3x" className="text-success mb-3" />
          <h3 className="text-success">¡Validaste tu identidad!</h3>
          <p className="lead">
            Validamos tu identidad.
            Estamos siguiendo con tu compra...
          </p>
          <div className="spinner-border text-primary mt-3" role="status">
            <span className="visually-hidden">Procesando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="identity-validation-step bg-white p-4 rounded-3">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p>Preparando para que valides tu identidad...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="identity-validation-step bg-white p-4 rounded-3">
      <div className="text-center mb-4">
        <h3>Validá tu identidad</h3>
      </div>

      <div className="step-body">
        {/* Información para validación externa */}
        <div className="text-center mb-4">
          <p className="lead">
            Es un paso rápido y seguro para seguir con tu compra.
          </p>

          <button
            className="btn btn-primary mt-4"
            onClick={validationError ? fetchValidationLink : handleExternalValidation}
            disabled={!validationData.verification_url && !validationError}
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
            {validationError ? 'Reintentar' : (validationData.verification_url ? 'Validá tu identidad' : 'Cargando...')}
          </button>

          {/* Enlace de testing discreto (solo visible en QA) */}
          {validationData.testing_url && (
            <div className="mt-3">
              <a 
                href={validationData.testing_url} 
                rel="noopener noreferrer" 
                className="text-muted small text-decoration-none"
                style={{ fontSize: '0.875rem' }}
              >
                Saltar a paso de redirección
              </a>
            </div>
          )}
        </div>

        {/* Error de validación externa */}
        {validationError && (
          <div className="alert alert-light-danger">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            {validationError}
          </div>
        )}
      </div>
    </div>
  );
};

export default IdentityValidationStep;
